import { BasePrice, Currency, GroupedProductRelationDto, NutritionalWarning, PricingDto, RelatedProductsTypeEnum, StatusEnum, Unit } from "./product-v2";

export interface ProductBaseV3 {
  id: string;
  name: string;
  status: 'ACTIVE' | 'INACTIVE';
  productRefId?: string;
}

export interface BrandV3 {
  id: string;
  name: string;
}

export interface CategoryV3 {
  id: string;
  name: string;
  externalId?: string;
}
export interface ProductSkuV3 {
  id: string;
  skuRefId: string;
  ean?: string;
  name: string;
  status: 'ACTIVE' | 'INACTIVE';
  skuType: 'REGULAR' | 'VARIANT';
}
export interface ProductWithSkusV3 extends ProductBaseV3 {
  brand: BrandV3;
  category: CategoryV3;
  salesContexts: { id: string; name: string }[];
  sellerId: string;
  skus: ProductSkuV3[];
}
export interface PageInfo {
  size: number;
  totalElements: number;
  totalPages: number;
  number: number;
}

export interface ProductsWithSkusPageV3 {
  items: ProductWithSkusV3[];
  page: PageInfo;
}
export interface ProductListResponseV3 {
  items: ProductBaseV3[];
  page: PageInfo;
}
export interface CreateProductDtoV3 {
  name: string;
  description?: string;
  brand: string;
  category: string;
  productRefId?: string;
  showWithoutStock?: boolean;
  visibility?: 'PUBLISHED' | 'CHECKOUT_ONLY';
  keywords?: string;
  permalink?: string;
  metaTitle?: string;
  metaDescription?: string;
  deepLink?: string;
  releaseDate?: string;
  score?: number;
  salesContexts?: string[];
}
export interface UpdateProductDtoV3 extends Partial<CreateProductDtoV3> {}
export interface CreateProductResponseV3 {
  id: string;
}
export interface ProductSkuIdsListResponseV3 {
  items: {
    productId: string;
    skuIds: string[];
  }[];
  page: PageInfo;
}
export enum ProductStatusV3 {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum ProductVisibilityV3 {
  PUBLISHED = 'PUBLISHED',
  CHECKOUT_ONLY = 'CHECKOUT_ONLY'
}
export interface ProductSalesContextDetailV3 {
  id: string;
  name: string;
}
export interface ProductSeoV3 {
  metaTitle?: string;
  metaDescription?: string;
  keywords?: string;
  permalink?: string;
}

export interface ProductDetailV3 {
  id: string;
  name: string;
  description: string;
  status: ProductStatusV3;

  brand: BrandV3;
  category: CategoryV3;

  productRefId: string;

  seo: ProductSeoV3;

  releaseDate?: string;

  score: number;

  showWithoutStock: boolean;

  visibility: ProductVisibilityV3;

  salesContexts: ProductSalesContextDetailV3[];

  sellerId: string;
}

export interface ListProductParamsV3 {
  page?: number;
  size?: number;

  /**
   * Formato: ['name,asc', 'createdAt,desc']
   */
  sort?: string[];

  /**
   * Búsqueda libre por nombre y descripción
   */
  q?: string;

  categoryId?: string;
  brandId?: string;

  status?: ProductStatusV3;

  /**
   * Estos se envían como:
   * productIds=1,2,3
   */
  productIds?: string[];

  skuIds?: string[];

  branchIds?: string[];
}
export function buildV3ProductParams(
  params: ListProductParamsV3
): any {
  const query: any = { ...params };

  if (params.productIds?.length) {
    query.productIds = params.productIds.join(',');
  }

  if (params.skuIds?.length) {
    query.skuIds = params.skuIds.join(',');
  }

  if (params.branchIds?.length) {
    query.branchIds = params.branchIds.join(',');
  }

  return query;
}
// product-list-item.vm.ts
export interface ProductListItemVM {
  id: string;
  name: string;
  sku?: string;

  categoryName?: string;
  category?: CategoryV3;
  productRefId?: string
  status: 'ACTIVE' | 'INACTIVE';

  imageUrl?: string;

  isBundle?: boolean;

  // flags UI
  canEdit: boolean;
  canManageBundle: boolean;
}
export interface ProductListVMResponse {
  items: ProductListItemVM[];
  total: number;
}
export const VISIBILITY_OPTIONS = [
  { code: 'PUBLISHED', label: 'Publicado' },
  { code: 'CHECKOUT_ONLY', label: 'Solo en checkout' },
] as const;

export type VisibilityOption = typeof VISIBILITY_OPTIONS[number];
export interface ProductUIVM {
  /* ===========================
   * Identidad
   * =========================== */
  storeId?: string;
  id: string;
  externalId?: string;
  productRefId?: string;
  basePrice?: BasePrice;

  name: string;
  description?: string;
  sku?: string;
  ean?: string;
  visibility?: VisibilityOption;
  status: StatusEnum;

  /* ===========================
   * Marca / Categoría
   * =========================== */
  brand: {
    id?: string;
    name?: string;
  };

  category: {
    id?: string;
    name?: string;
    externalId?: string;
  };

  /* ===========================
   * SEO
   * =========================== */
  seo: {
    keywords?: string;
    permalink?: string;
    metaTitle?: string;
    metaDescription?: string;
  };

  /* ===========================
   * Imágenes
   * =========================== */
  images: Array<{
    url: string;
    altText?: string;
    order: number
  }>;

  mainImageUrl?: string;
  
  /* ===========================
  * Precios
  * =========================== */
  pricing: PricingDto;
  

  discount?: {
    type: 'FIXED' | 'PERCENTAGE';
    value: number;
    expiration?: string;
    branchId?: string | null;
  };

  /* ===========================
   * Stock
   * =========================== */
  stock: {
    unit?: Unit;
    quantity?: number;
    showWithoutStock?: boolean;
    totalStock?: number;
    branches?: Array<{
      branchId: string;
      branchName?: string | null;
      quantity: number;
      stockAvailable: number;
      stockReserved: number;
      stockFulfilled: number;
    }>;
  };

  /* ===========================
   * Dimensiones
   * =========================== */
  dimensions: {
    heightInCm?: number;
    widthInCm?: number;
    lengthInCm?: number;
    weightInGrams?: number;
  };

  /* ===========================
   * Especificaciones
   * =========================== */
  specifications?: Array<{
    attribute: string;
    attributeLabel: string;
    value: string | number;
  }>;

  /* ===========================
   * Nutrición
   * =========================== */
  nutritionalWarnings?: NutritionalWarning[];

  /* ===========================
   * Relaciones
   * =========================== */
    productRelations?: GroupedProductRelationDto[] | null;
  

  /* ===========================
   * Bundle
   * =========================== */
  isBundle?: boolean;
  bundleItems?: Array<{
    productId: string;
    quantity: number;
    unitPrice: number;
  }>;

  /* ===========================
   * Metadata
   * =========================== */
  score?: number;
  releaseDate?: string;
  salesContexts?: string[];

  /* ===========================
   * Flags UI (NO backend)
   * =========================== */
  canEdit: boolean;
  canManageBundle: boolean;
}

export function createEmptyProductUIVM(): ProductUIVM {
  return {
    id: '',
    name: '',
    description: '',
    productRefId: '',
    sku: '',
    ean: '',
    status: StatusEnum.INACTIVE,
    salesContexts: [],
    visibility: VISIBILITY_OPTIONS[0],
    brand: {
      id: '',
      name: ''
    },

    category: {
      id: '',
      name: ''
    },

    seo: {
      keywords: '',
      permalink: '',
      metaTitle: '',
      metaDescription: ''
    },

    images: [],
    mainImageUrl: '',

    pricing: {
      base: {
        amount: 0,
        currency: Currency.PEN
      },
      branches: []
    },

    specifications: [],
    nutritionalWarnings: [],

    stock: {
      quantity: 0,
      showWithoutStock: true,
      branches: []
    },

    isBundle: false,
    bundleItems: [],

    dimensions: {
      heightInCm: 0,
      widthInCm: 0,
      lengthInCm: 0,
      weightInGrams: 0
    },

    canEdit: true,
    canManageBundle: false
  };
}
